import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";

export type OverdueSale = {
  saleId: string;
  buyerFullName: string;
  buyerMobile?: string;
  phone?: string;
  buyerEmail?: string;
  overdueInstallmentCount: number;
  maxDaysOverdue: number;
  totalOutstandingBalance: number;
  paymentStatus: string;
  overdueInstallments?: Array<{ description?: string; balance?: number; dueDate?: string; daysOverdue?: number }>;
};

type Props = {
  overdueSales: OverdueSale[];
  totalContacts: number;
  selectedSaleIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onContinue: () => void;
  maxSelectable?: number;
};

const RecaudoResults = ({
  overdueSales,
  totalContacts,
  selectedSaleIds,
  onSelectionChange,
  onContinue,
  maxSelectable,
}: Props) => {
  const selectableSales = overdueSales.filter((v) => v.buyerMobile || v.phone);
  const effectiveMax = maxSelectable ?? selectableSales.length;
  const allSelected =
    selectableSales.length > 0 && selectedSaleIds.length === Math.min(selectableSales.length, effectiveMax);
  const someSelected = selectedSaleIds.length > 0 && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(selectableSales.slice(0, effectiveMax).map((v) => v.saleId));
    }
  };

  const handleToggle = (id: string) => {
    if (selectedSaleIds.includes(id)) {
      onSelectionChange(selectedSaleIds.filter((i) => i !== id));
    } else if (selectedSaleIds.length < effectiveMax) {
      onSelectionChange([...selectedSaleIds, id]);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Resultados</CardTitle>
        <CardDescription>
          Contactos en mora que coinciden con los conceptos seleccionados
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-lg font-semibold text-palomma-primary">
            {totalContacts} {totalContacts === 1 ? "contacto" : "contactos"} en mora
          </p>
          {maxSelectable !== undefined && (
            <p className="text-sm font-medium text-orange-600">
              {Math.max(0, effectiveMax - selectedSaleIds.length)}/{effectiveMax} líneas disponibles
            </p>
          )}
        </div>
        {overdueSales.length > 0 && (
          <>
            <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
              <Checkbox
                checked={someSelected ? "indeterminate" : allSelected}
                onCheckedChange={handleSelectAll}
                id="select-all"
                disabled={effectiveMax === 0}
              />
              <label htmlFor="select-all" className="cursor-pointer text-sm font-medium text-gray-700">
                Seleccionar todos ({Math.min(selectableSales.length, effectiveMax)} con teléfono
                {maxSelectable !== undefined ? ", disponibles" : ""})
              </label>
            </div>
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {overdueSales.map((v) => {
                const hasPhone = !!(v.buyerMobile || v.phone);
                const isSelected = selectedSaleIds.includes(v.saleId);
                return (
                  <div
                    key={v.saleId}
                    className={`flex items-start gap-3 rounded-md border px-4 py-3 ${
                      hasPhone
                        ? "border-gray-200 cursor-pointer hover:bg-gray-50"
                        : "border-gray-100 opacity-50"
                    }`}
                    onClick={() =>
                      hasPhone &&
                      (isSelected || selectedSaleIds.length < effectiveMax) &&
                      handleToggle(v.saleId)
                    }
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => hasPhone && handleToggle(v.saleId)}
                      disabled={!hasPhone || (!isSelected && selectedSaleIds.length >= effectiveMax)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900">{v.buyerFullName}</p>
                      {hasPhone ? (
                        <p className="text-sm text-gray-600">{v.buyerMobile ?? v.phone}</p>
                      ) : (
                        <p className="text-sm text-gray-400 italic">Sin teléfono registrado</p>
                      )}
                      {v.buyerEmail && (
                        <p className="text-sm text-gray-600">{v.buyerEmail}</p>
                      )}
                      <p className="text-xs text-gray-500">
                        {v.overdueInstallmentCount} cuota(s) en mora
                        {v.maxDaysOverdue != null && ` · ${v.maxDaysOverdue} día(s) de mora`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        <div className="flex items-center justify-between">
          {selectedSaleIds.length > 0 && (
            <p className="text-sm text-gray-600">
              {selectedSaleIds.length} de {selectableSales.length} seleccionados
            </p>
          )}
          <Button
            onClick={onContinue}
            disabled={selectedSaleIds.length === 0}
            className={selectedSaleIds.length === 0 ? "" : "ml-auto"}
          >
            Continuar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default RecaudoResults;
